import { Box, Grid, Paper, Typography, useTheme } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";

import { BasicPieChart } from "@aclymatepackages/modules";
import { DefaultPaper } from "@aclymatepackages/atoms";
import { subcategoriesWithColors } from "@aclymatepackages/subcategories";
import { formatDecimal } from "@aclymatepackages/formatters";

const PIE_ASPECT = 1.5;
const BAR_LINE_ASPECT = 2.5;
const CHART_MAX_WIDTH = 480;

const normalizeKey = (value) =>
  (value || "").toString().trim().toLowerCase().replace(/\s+/g, "-");

const subcategoryColorByName = subcategoriesWithColors.reduce(
  (acc, { name, subcategory, color }) => ({
    ...acc,
    [normalizeKey(name)]: color,
    [normalizeKey(subcategory)]: color
  }),
  {}
);

const buildFallbackPalette = (palette) => [
  palette.primary.main,
  palette.secondary.main,
  palette.primary.dark,
  palette.secondary.dark,
  palette.flights?.main || palette.primary.light,
  palette.employees?.main || palette.secondary.light,
  palette.offices?.main || palette.backgroundGray.dark,
  palette.vendors?.main || palette.text.secondary
];

const colorForSlice = ({ label, categoryKey }, idx, fallback) => {
  const fromKey = subcategoryColorByName[normalizeKey(categoryKey)];
  if (fromKey) return fromKey;
  const fromLabel = subcategoryColorByName[normalizeKey(label)];
  if (fromLabel) return fromLabel;
  return fallback[idx % fallback.length];
};

const ChartTooltipBody = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const [{ payload: slice }] = payload;
  return (
    <DefaultPaper>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Box
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: slice.color
            }}
          />
        </Grid>
        <Grid item>
          <Typography variant="subtitle2" color="textPrimary">
            {slice.label}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatDecimal(slice.value)}
            {slice.percent ? ` (${formatDecimal(slice.percent * 100)}%)` : ""}
          </Typography>
        </Grid>
      </Grid>
    </DefaultPaper>
  );
};

const BarChartBody = ({ data, fontFamily }) => (
  <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="label" fontSize="small" style={{ fontFamily }} />
    <YAxis
      fontSize="small"
      style={{ fontFamily }}
      tickFormatter={(tick) => formatDecimal(tick)}
    />
    <Tooltip
      wrapperStyle={{ zIndex: 99 }}
      content={(props) => <ChartTooltipBody {...props} />}
    />
    <Bar dataKey="value" isAnimationActive={false}>
      {data.map((slice, idx) => (
        <Cell key={`scout-bar-cell-${idx}`} fill={slice.color} />
      ))}
    </Bar>
  </BarChart>
);

const LineChartBody = ({ data, palette, fontFamily }) => (
  <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="label" fontSize="small" style={{ fontFamily }} />
    <YAxis
      fontSize="small"
      style={{ fontFamily }}
      tickFormatter={(tick) => formatDecimal(tick)}
    />
    <Tooltip
      wrapperStyle={{ zIndex: 99 }}
      content={(props) => <ChartTooltipBody {...props} />}
    />
    <Line
      type="monotone"
      dataKey="value"
      stroke={palette.primary.main}
      strokeWidth={2}
      dot={{ fill: palette.primary.main }}
      isAnimationActive={false}
    />
  </LineChart>
);

const Legend = ({ data }) => (
  <Grid container spacing={1}>
    {data.map((slice, idx) => (
      <Grid item key={`scout-chart-legend-${idx}`} xs={6} sm={4} md={3}>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Box
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: slice.color,
              flexShrink: 0
            }}
          />
          <Typography variant="caption" color="textPrimary" noWrap>
            {slice.label}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>
);

const inlineChartBodyMap = {
  bar: BarChartBody,
  line: LineChartBody
};

const ScoutChartCard = ({ chartType, title, series, footnote }) => {
  const { palette, typography } = useTheme();
  const fallback = buildFallbackPalette(palette);

  const total = series.reduce(
    (sum, { value }) => sum + (Number(value) || 0),
    0
  );
  const data = series.map((slice, idx) => ({
    ...slice,
    color: colorForSlice(slice, idx, fallback),
    percent: total > 0 ? (Number(slice.value) || 0) / total : 0
  }));

  const InlineChartBody = inlineChartBodyMap[chartType];
  const isPie = chartType === "pie";

  if (!isPie && !InlineChartBody) return null;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: 520
      }}
    >
      <Typography variant="subtitle1" color="textPrimary">
        {title}
      </Typography>
      <Box
        position="relative"
        style={{ width: "100%", maxWidth: CHART_MAX_WIDTH, minWidth: 0 }}
      >
        {isPie ? (
          <BasicPieChart data={data} aspect={PIE_ASPECT} />
        ) : (
          <ResponsiveContainer width="100%" aspect={BAR_LINE_ASPECT}>
            <InlineChartBody
              data={data}
              palette={palette}
              fontFamily={typography.fontFamily}
            />
          </ResponsiveContainer>
        )}
      </Box>
      {isPie && <Legend data={data} />}
      {footnote && (
        <Typography variant="caption" color="textSecondary">
          {footnote}
        </Typography>
      )}
    </Paper>
  );
};

export default ScoutChartCard;
